import { useCallback } from "react";

import useTypedContext from "hooks/useTypedContext";
import { Action, Stack } from "types/generated";
import { AccessResult, hasAtLeastOneRequiredAction, NO_ACCESS_MESSAGE } from "utils/access";
import { AccountContext } from "views/AccountWrapper";

import { checkTemplateDeploymentAccess } from "./helpers/checkTemplateDeploymentAccess";
import useHasStackAdminAccess from "./useHasStackAdminAccess";

/**
 * Hook that returns a function to check if the user can disable a stack.
 * User needs to have SpaceAdmin or StackDisable action in stack's space
 * Stacks created from a template deployment (blueprintDeployment) cannot be disabled.
 * @returns {(stack: Stack) => AccessResult} A function that takes a stack and returns an AccessResult indicating
 * whether the user can disable the stack, with an error message if applicable.
 */
const useCanDisableStack = (): ((stack: Stack) => AccessResult) => {
  const { viewer } = useTypedContext(AccountContext);

  const hasStackAdminAccess = useHasStackAdminAccess();

  return useCallback(
    (stack: Stack) => {
      const blueprintCheck = checkTemplateDeploymentAccess(stack);
      if (blueprintCheck) {
        return blueprintCheck;
      }

      if (stack.isDisabled) {
        return { result: false };
      }

      if (hasStackAdminAccess(stack).result) {
        return { result: true };
      }

      if (
        hasAtLeastOneRequiredAction(
          [Action.StackDisable],
          viewer,
          stack.spaceDetails?.id || stack.space
        )
      ) {
        return { result: true };
      }

      return { result: false, message: NO_ACCESS_MESSAGE };
    },
    [hasStackAdminAccess, viewer]
  );
};

export default useCanDisableStack;
